/**
 * src/admin-ui/src/components/Footer.tsx
 *
 * Responsive application navigation and global actions.
 */

import React, { useEffect, useRef, useState } from "react";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { getNavigationItems } from "../navigation";
import ThemeToggle from "./ThemeToggle";
import {
  DesktopNavigation,
  FooterActions,
  FooterBar,
  FooterToolbar,
  MobileNavigation,
  NavigationButton,
} from "./Footer.styles";

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const { isAuthenticated, logout, user } = useAuthStore();
  const footerRef = useRef<HTMLElement | null>(null);

  // Publish the real footer height so fixed elements (e.g. the analytics
  // settings bar) can dock flush against it — the rendered height varies
  // with viewport width and platform, so a hardcoded constant drifts.
  useEffect(() => {
    const node = footerRef.current;
    if (!node) return undefined;
    const updateHeight = () => {
      document.documentElement.style.setProperty(
        "--app-footer-height",
        `${node.offsetHeight}px`,
      );
    };
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const navItems = getNavigationItems(user);

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  return (
    <FooterBar ref={footerRef} position="fixed" elevation={2}>
      <FooterToolbar>
        <FooterActions>
          <ThemeToggle />
          {isAuthenticated && (
            <Tooltip title="Выйти">
              <IconButton
                color="inherit"
                onClick={handleLogout}
                aria-label="Выйти"
                className="desktop-logout"
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          )}
        </FooterActions>

        <DesktopNavigation aria-label="Основная навигация">
          {navItems.map((item) => (
            <NavigationButton
              key={item.path}
              active={location.pathname === item.path}
              aria-current={
                location.pathname === item.path ? "page" : undefined
              }
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </NavigationButton>
          ))}
        </DesktopNavigation>

        <MobileNavigation>
          <IconButton
            color="inherit"
            onClick={handleMenuOpen}
            aria-label="Открыть меню"
            aria-controls={anchorEl ? "mobile-navigation-menu" : undefined}
            aria-expanded={Boolean(anchorEl)}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            id="mobile-navigation-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {navItems.map((item) => (
              <MenuItem
                key={item.path}
                selected={location.pathname === item.path}
                onClick={() => {
                  handleMenuClose();
                  navigate(item.path);
                }}
              >
                {item.label}
              </MenuItem>
            ))}
            {isAuthenticated && (
              <MenuItem onClick={handleLogout}>Выход</MenuItem>
            )}
          </Menu>
        </MobileNavigation>
      </FooterToolbar>
    </FooterBar>
  );
};

export default Footer;
